/**
 * © 2025 Grupo Salamander SAPI de CV
 *
 * Typography configuration for the Renaissance design system.
 *
 * Ported from the approved redesign canvas (plan task 1.1, formerly
 * `Theme/next/typography.ts`). The face is Lato — the brand face, decided on
 * the canvas 2026-09-18 — and every size derives from the density token, so
 * no size here is a literal for one density.
 *
 * Two shipped bugs are fixed by the port: `caption` had `lineHeight: 0.75`
 * (a line box smaller than its glyphs), and `title`/`subtitle` promised an
 * ellipsis via `-webkit-box` without `WebkitLineClamp`/`WebkitBoxOrient`, so
 * it never fired. Ten bold roles above body are collapsed to five, varying
 * weight as well as size.
 *
 * Plan task 1.5 (2026-09-23): each portal app now self-hosts Lato via
 * `next/font/google` in its `layout.tsx` (400/700 upright, `latin` subset —
 * the only weight/style combinations this file declares), mirroring
 * `apps/hyperseguros-web`'s existing approach. next/font keeps the literal
 * "Lato" family name and adds an automatic size-adjusted fallback face, so
 * `FONT_FAMILY` below needs no change and cold loads take no
 * `fonts.googleapis.com` request. See `Typography.stories.tsx` for the
 * type-scale proof at 12/14/16/20/32px in both themes.
 */

import type { TPaletteDefinition } from './palette'
import { density, numeric, type TDensity } from './tokens'

const FONT_FAMILY =
  '"Lato", -apple-system, BlinkMacSystemFont, "Helvetica Neue", system-ui, sans-serif'

function role(
  size: number,
  weight: number,
  lineHeight: number,
  tracking: number,
  color: string
) {
  return {
    fontFamily: FONT_FAMILY,
    fontSize: `${size / 16}rem`,
    fontWeight: weight,
    lineHeight,
    letterSpacing: `${tracking}em`,
    color,
  }
}

export function typographyConfig(
  palette: TPaletteDefinition,
  densityKey: TDensity = 'regular'
) {
  const base = density[densityKey].fontSize
  const ink = palette.text.main
  const muted = palette.textSecondary.main

  return {
    fontFamily: FONT_FAMILY,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,

    // ── Above body: five roles ──────────────────────────────────────────
    /** Page title. One per screen. */
    h1: role(base + 14, 600, 1.15, -0.02, ink),
    /** Section title within a screen. */
    h2: role(base + 8, 600, 1.2, -0.015, ink),
    /** Card or panel heading. */
    h3: role(base + 4, 600, 1.3, -0.008, ink),
    /** Group heading inside a panel; the densest heading level. */
    h4: role(base + 1, 600, 1.4, -0.002, ink),
    /** Field and column labels. Sentence case, not tracked. */
    h5: role(base - 1, 500, 1.4, 0, muted),

    // MUI requires h6/subtitle/overline to exist. Aliased rather than given
    // independent values, so the five-role hierarchy cannot drift back to ten.
    h6: role(base + 1, 600, 1.4, -0.002, ink),
    subtitle1: role(base, 500, 1.5, 0, ink),
    subtitle2: role(base - 1, 500, 1.5, 0, muted),
    overline: role(base - 3, 500, 1.6, 0, muted),

    // ── Body and utility ────────────────────────────────────────────────
    body1: role(base, 400, 1.55, 0, ink),
    body2: role(base - 1, 400, 1.5, 0, ink),
    /** Was `lineHeight: 0.75`, which clipped captions. */
    caption: role(base - 2, 400, 1.35, 0.01, muted),
    /**
     * Kept at the pre-redesign metrics on purpose: hyperseguros-web's nav pill
     * is a `UIButton`, and plan decision D3 makes that pill the reference the
     * portal's pills follow — so the label must not drift toward the canvas's
     * tighter 0.005em button tracking. Not density-derived for the same reason.
     */
    button: {
      fontSize: 14,
      fontWeight: 500,
      lineHeight: 1.75,
      letterSpacing: '0.02em',
      textTransform: 'none' as const,
      color: ink,
    },

    // ── Data ────────────────────────────────────────────────────────────
    /** Any figure in a column: premiums, sums insured, counts, ids. */
    numeric: { ...role(base, 400, 1.5, 0, ink), ...numeric },
    /** Emphasised figure — a stat tile, a total row. */
    numericStrong: { ...role(base + 6, 600, 1.2, -0.01, ink), ...numeric },

    /** Single-line truncation that actually truncates. */
    title: {
      ...role(base + 1, 600, 1.35, -0.002, ink),
      overflow: 'hidden' as const,
      textOverflow: 'ellipsis' as const,
      whiteSpace: 'nowrap' as const,
    },
    subtitle: {
      ...role(base - 1, 400, 1.4, 0, muted),
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical' as const,
      overflow: 'hidden' as const,
    },
  }
}
